import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { Observable, catchError, of, map, throwError } from 'rxjs';
import { normalizeFilterRequestsForBackend } from '../shared/appUtil';
import { serializeFilterOperandValue } from '../shared/constant';
import { normalizeCategoryArray, normalizeChartDisplayLabel } from '../shared/chart-label.util';
import type { FilterRequest } from '../shared/types';

export interface ChartType {
  id: string;
  name: string;
  description: string;
  icon?: string;
  availability_status?: boolean;
}

export interface Metric {
  id: string;
  name: string;
  description?: string;
}

export interface CriteriaOption {
  field: string;
  label: string;
  type: 'number' | 'string' | 'select' | 'boolean';
  options?: any[];
  min?: number;
  max?: number;
  default?: any;
}

export interface ChartCriteria {
  /** Set to null when the user clears Top N so the API receives an explicit empty value. */
  top_n?: number | null;
  time_bucket?: string;
  sort_order?: string;
  sort_by?: string | null;
  stacked?: boolean;
  date_range_type?: 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year' | 'last_n_days' | 'custom_range' | null;
  last_n_days?: number | null;
  // Pie/Donut Chart Options
  show_percentage?: boolean;
  min_slice_percentage?: number; // 0-10
  inner_size?: string; // e.g., "50%" for donut chart
  // Bubble/Scatter Chart Options
  bubble_size?: 'fixed' | 'proportional';
  min_bubble_size?: number;
  max_bubble_size?: number;
  // Treemap Options
  layout_algorithm?: 'sliceAndDice' | 'stripes' | 'squarified' | 'strip';
  // Sankey Options
  min_link_width?: number;
  node_padding?: number;
  // Waterfall Options
  show_connectors?: boolean;
  // Polar/Radar Options
  start_angle?: number; // 0-360 degrees
  // Gauge Options
  min_value?: number;
  max_value?: number;
  thresholds?: Array<{
    value: number;
    color: string;
    label?: string;
  }>;
  // Heatmap Options
  color_scale?: 'sequential' | 'diverging';
  [key: string]: any;
}

export interface Filter {
  field: string;
  operator: string;
  value: any;
}

export interface GenerateChartRequest {
  chart_type: string;
  metric_type: string;
  start_date?: string; // Optional when using dynamic date ranges
  end_date?: string; // Optional when using dynamic date ranges
  dimension?: string;
  secondary_dimension?: string | null;
  filters?: FilterRequest[];
  criteria?: ChartCriteria;
  chart_title?: string | null;
  chart_subtitle?: string | null;
  x_axis_name?: string | null; // Custom X-axis label (from API docs)
  y_axis_name?: string | null; // Custom Y-axis label (from API docs)
  time_zone?: string; // Time zone for chart data (e.g. EST); default EST per Insights dashboard
  /** Same as time_zone; present on some GET chart-for-edit responses */
  timeZone?: string;
}

export interface GenerateChartResponse {
  chart_id?: string;
  data?: any;
  categories?: any[];
  [key: string]: any;
}

export interface ChartData {
  id?: string;
  title: string;
  chart_type: string;
  data: any;
  config: any;
  table_data?: any[]; // Tabular view of chart data
  last_updated_timestamp?: string; // ISO 8601 timestamp from backend
  updated_at?: string | null; // ISO 8601 timestamp from backend
  generated_at?: string; // ISO 8601 timestamp from backend
  last_edited_at?: string | null; // Last config edit time (LastEditedAt, else UpdatedAt)
  generated_by?: string; // Creator email (CreatedByEmail)
  updated_by?: string; // Last editor email (UpdatedByEmail)
  time_zone?: string; // Chart's selected timezone (e.g. EST) for displaying timestamps
  DurationBehavior?: string; // Duration behavior from API: "fixed", "dynamic_daily", "dynamic_weekly", "dynamic_monthly", "dynamic_quarterly", "dynamic_last_n_days"
  duration_behavior?: string; // Alternative naming (snake_case)
  durationBehavior?: string; // Alternative naming (camelCase)
  no_data_found?: boolean; // Flag indicating if no data was found for this chart
  filters?: FilterRequest[]; // Applied filters for this chart (Insights FilterRequest shape)
  [key: string]: any; // Allow additional properties from API
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }
    const text = String(value).trim();
    if (text) {
      return text;
    }
  }
  return undefined;
}

/**
 * Map audit/metadata fields from a custom-dashboard chart API object.
 * Works for both regular-update (dynamic) and fixed-range charts.
 */
export function extractChartAuditFields(chart: any): Pick<
  ChartData,
  | 'last_updated_timestamp'
  | 'updated_at'
  | 'generated_at'
  | 'last_edited_at'
  | 'generated_by'
  | 'updated_by'
  | 'time_zone'
> {
  if (!chart) {
    return {};
  }

  const generatedAt = firstNonEmptyString(chart.CreatedAt, chart.created_at, chart.generated_at);
  const updatedAt = firstNonEmptyString(chart.UpdatedAt, chart.updated_at);
  const lastDataUpdatedAt = firstNonEmptyString(
    chart.LastGeneratedAt,
    chart.last_generated_at,
    chart.lastGeneratedAt,
    chart.LastUpdatedAt,
    chart.last_updated_at,
    updatedAt,
    // Edit regenerates data, so a new LastEditedAt is also a data refresh when LastGeneratedAt is absent.
    chart.LastEditedAt,
    chart.last_edited_at,
    chart.lastEditedAt,
    generatedAt
  );
  // Legacy charts have no LastEditedAt. Use UpdatedAt so last edited stays the last save,
  // not create time. After Sync, LastEditedAt is stored and preferred.
  const lastEditedAt = firstNonEmptyString(
    chart.LastEditedAt,
    chart.last_edited_at,
    chart.lastEditedAt,
    chart.EditedAt,
    chart.edited_at,
    updatedAt,
    generatedAt
  );
  const generatedBy = firstNonEmptyString(
    chart.CreatedByEmail,
    chart.created_by_email,
    chart.generated_by
  );
  const updatedBy = firstNonEmptyString(
    chart.LastEditedByEmail,
    chart.last_edited_by_email,
    chart.LastEditedBy,
    chart.last_edited_by,
    chart.UpdatedByEmail,
    chart.updated_by_email,
    generatedBy
  );
  const timeZone = firstNonEmptyString(chart.TimeZone, chart.time_zone, chart.timeZone);

  return {
    last_updated_timestamp: lastDataUpdatedAt,
    updated_at: updatedAt ?? null,
    generated_at: generatedAt,
    last_edited_at: lastEditedAt ?? null,
    generated_by: generatedBy,
    updated_by: updatedBy,
    time_zone: timeZone
  };
}

/**
 * Format Azure AD / email identifiers for dashboard audit display.
 * Returns empty string when missing so callers can hide the value.
 */
export function formatDashboardUserEmail(email?: string | null): string {
  if (!email) {
    return '';
  }

  if (email.startsWith('AzureAD_')) {
    const extIndex = email.indexOf('#EXT#');
    if (extIndex > 0) {
      return email.substring(8, extIndex);
    }
  }

  if (email.includes('@')) {
    return email.split('@')[0];
  }

  return email;
}

export interface Dashboard {
  id?: string;
  name: string;
  charts: ChartData[];
  layout?: string;
  created_at?: string;
  updated_at?: string;
  status?: 'active' | 'inactive' | 'archived';
  is_shared_with_all?: boolean;
  is_shared?: boolean;
  owner_id?: string | null;
  generated_by?: string; // user_id
  generated_by_email?: string; // email address (from UserEmail)
  updated_by?: string | null; // user_id
  updated_by_email?: string | null; // email address (from UpdatedByEmail)
  user_email?: string; // Created by email (from UserEmail)
}

export interface SaveDashboardRequest {
  dashboard_name: string;
  chart_ids: string[];
  layout?: string;
  status?: 'active' | 'inactive' | 'archived';
}

export interface SaveDashboardResponse {
  dashboard_id: string;
  message?: string;
}

export interface DashboardDetailsResponse {
  dashboard_id: string;
  dashboard_name: string;
  layout?: string;
  charts: ChartData[];
  created_at?: string;
  updated_at?: string;
}

export interface ExportChartToCSVResponse {
  bucket_name: string;
  file_key: string;
  download_url: string;
  file_size: number;
  row_count: number;
}

export interface Dimension {
  key: string; // Key to use in API payload
  value: string; // Display name to show to users
  data_type: 'string' | 'number' | 'date' | 'boolean';
  available: boolean; // Whether this dimension is available
}

export interface DimensionsResponse {
  success: boolean;
  message: string;
  data: {
    dimensions?: Dimension[];
    [key: string]: any; // Allow for different response structures
  };
}

export interface CreateExportJobResponse {
  success: boolean;
  message: string;
  data: {
    bucket_name: string;
    file_key: string;
    job_id: string;
  };
}

export interface CheckCSVFileRequest {
  bucket_name: string;
  file_key: string;
}

export interface CheckCSVFileResponse {
  success: boolean;
  message: string;
  data: {
    available?: boolean;
    ready_to_download?: boolean; // API returns this field
    download_url?: string;
    expires_at?: string; // timestamp
  };
}

@Injectable({
  providedIn: 'root',
})
export class UserDashboardService {
  private _createDashboardEnabled = signal<boolean>(false);
  private baseUrl = process.env['API_DOMAIN_NAME'];
  private customDashboardBasePath = 'trends/custom-dashboard';

  constructor(private http: HttpClient) {}

  public get createDashboardEnabled(): WritableSignal<boolean> {
    return this._createDashboardEnabled;
  }

  setCreateDashboardEnabled(enabled: boolean): void {
    this._createDashboardEnabled.set(enabled);
  }

  private getAuthHeaders(): HttpHeaders {
    const accessToken = localStorage.getItem('authToken');
    if (!accessToken) {
      throw new Error('Access token not available.');
    }
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: accessToken,
    });
  }

  private handleError(error: any): Observable<any> {
    console.error('User Dashboard API Error:', error);
    // Return empty array instead of throwing to allow fallback handling
    if (error.status === 0 || error.status >= 400) {
      // console.warn('API call failed, returning empty array for fallback');
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }
    throw error;
  }

  // Get all available dimensions
  getDimensions(): Observable<DimensionsResponse> {
    try {
      return this.http
        .get<DimensionsResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/dimensions`, {
          headers: this.getAuthHeaders(),
        })
        .pipe(
          map((response: any) => {
            // Handle different response structures
            let dimensionsArray: Dimension[] = [];

            // Check for wrapped format: { success, message, data: { dimensions: [...] } }
            if (response.success && response.data && response.data.dimensions) {
              dimensionsArray = response.data.dimensions;
            }
            // Check for direct array in data: { data: [...] }
            else if (response.data && Array.isArray(response.data)) {
              dimensionsArray = response.data;
            }
            // Check for direct array: { dimensions: [...] }
            else if (response.dimensions && Array.isArray(response.dimensions)) {
              dimensionsArray = response.dimensions;
            }
            // Check for direct array response
            else if (Array.isArray(response)) {
              dimensionsArray = response;
            }
            // Check for object with keys as dimensions
            else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
              // Convert object to array if dimensions are stored as object
              dimensionsArray = Object.values(response.data).filter((item: any) =>
                item && (item.key || item.name || item.value)
              ) as Dimension[];
            }

            // Normalize dimension structure to ensure key/value format
            dimensionsArray = dimensionsArray.map((dim: any) => {
              // Handle different possible field names
              return {
                key: dim.key || dim.name || dim.field || '',
                value: dim.value || dim.display_name || dim.displayName || dim.name || '',
                data_type: dim.data_type || dim.dataType || dim.type || 'string',
                available: dim.available !== undefined ? dim.available : true
              };
            });

            return {
              success: true,
              message: `Retrieved ${dimensionsArray.length} dimensions`,
              data: { dimensions: dimensionsArray }
            };
          }),
          catchError((error) => {
            console.error('Error fetching dimensions:', error);
            // Return empty dimensions array on error
            return of({
              success: false,
              message: 'Failed to fetch dimensions',
              data: { dimensions: [] }
            } as DimensionsResponse);
          })
        );
    } catch (error) {
      console.error('Error setting up dimensions request:', error);
      // Return empty dimensions if headers can't be created (e.g., no auth token)
      return of({
        success: false,
        message: 'Failed to fetch dimensions',
        data: { dimensions: [] }
      } as DimensionsResponse);
    }
  }

  // Get all available chart types
  getChartTypes(): Observable<ChartType[]> {
    try {
      return this.http
        .get<ChartType[]>(`${this.baseUrl}/${this.customDashboardBasePath}/chart-types`, {
          headers: this.getAuthHeaders(),
        })
        .pipe(
          catchError((error) => {
            console.error('Error fetching chart types:', error);
            // Return empty array on error to trigger fallback
            return of([] as ChartType[]);
          })
        );
    } catch (error) {
      console.error('Error setting up chart types request:', error);
      // Return empty array if headers can't be created (e.g., no auth token)
      return of([] as ChartType[]);
    }
  }


  // Get criteria options for a chart type
  getCriteriaOptions(chartType: string): Observable<CriteriaOption[]> {
    return this.http
      .get<CriteriaOption[]>(`${this.baseUrl}/${this.customDashboardBasePath}/chart-types/${chartType}/criteria`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Generate chart
  generateChart(request: GenerateChartRequest | any): Observable<GenerateChartResponse> {
    // Clean the request to remove any undefined values and ensure proper types
    const cleanedRequest = this.cleanRequest(request);

    return this.http
      .post<GenerateChartResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/generate`, cleanedRequest, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        // Clean response to remove null values from categories before processing
        map((response: any) => this.cleanChartResponse(response)),
        catchError((error: any) => {
          // Never convert chart generate errors to success — so no card is shown on 4xx/5xx
          if (error?.error?.detail && typeof error.error.detail === 'string' && error.error.detail.includes('categories')) {
            const friendlyError = {
              ...error,
              error: {
                ...error.error,
                message: 'Chart generation failed due to invalid data. The server returned null values in categories. Please try again or contact support if the issue persists.',
                detail: 'The server returned invalid category data with null values. This is a known backend issue that needs to be fixed on the backend.'
              }
            };
            return throwError(() => friendlyError);
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Extract chart data from either API response format:
   * - Chart generation (/generate): response.data is the chart object
   * - Dashboard retrieval (/{id}): response.data.Charts is array; pass one chart or use when iterating
   * When given a full response, returns the single chart object (for generate) or the first chart (for dashboard).
   * When given a chart object (e.g. from dashboardData.Charts[i]), returns it as-is.
   */
  extractChartData(response: any): any {
    if (!response) {
      return response;
    }
    // Already a chart object (e.g. from dashboardData.Charts[i])
    if (response.ChartId != null || (response.ChartConfig && response.Series)) {
      return response;
    }
    if (response.data) {
      if (response.data.Charts && Array.isArray(response.data.Charts)) {
        return response.data.Charts[0] ?? response.data;
      }
      return response.data;
    }
    return response;
  }

  /**
   * Clean chart response to remove null/undefined values from categories array
   * This fixes backend validation errors where None values are present in categories
   * API uses PascalCase format: ChartId, ChartType, ChartConfig, Series, Categories, etc.
   */
  private cleanChartResponse(response: any): any {
    if (!response) {
      return response;
    }

    // Normalize to single chart object so we clean the same shape for both API formats
    const chartData = this.extractChartData(response);
    const dataRef = chartData || response?.data;
    if (!dataRef) {
      return response;
    }

    // Clean categories in nested data object (API uses PascalCase: Categories)
    if (dataRef.Categories && Array.isArray(dataRef.Categories)) {
      dataRef.Categories = normalizeCategoryArray(dataRef.Categories);
    }

    // Clean categories in ChartConfig if present (nested objects use camelCase: xAxis, yAxis)
    if (dataRef.ChartConfig) {
      if (dataRef.ChartConfig.xAxis?.categories && Array.isArray(dataRef.ChartConfig.xAxis.categories)) {
        dataRef.ChartConfig.xAxis.categories = normalizeCategoryArray(dataRef.ChartConfig.xAxis.categories);
      }
    }

    // Clean series data - remove null category references (API uses PascalCase: Series)
    if (dataRef.Series && Array.isArray(dataRef.Series)) {
      dataRef.Series = dataRef.Series.map((series: any) => {
          if (series && typeof series === 'object') {
            // Clean data points that might have null category references
            if (Array.isArray(series.data)) {
              series.data = series.data.map((point: any) => {
                // For bar/column charts, data might be just values, categories are separate
                // For pie charts, data might be [name, value] format
                if (Array.isArray(point) && point.length >= 2) {
                  const copy = [...point];
                  copy[0] = normalizeChartDisplayLabel(copy[0]);
                  return copy;
                }
                if (point && typeof point === 'object' && !Array.isArray(point)) {
                  const next = { ...point };
                  if ('name' in next || 'label' in next) {
                    next.name = normalizeChartDisplayLabel(next.name ?? next.label ?? '');
                    if ('label' in next) {
                      next.label = next.name;
                    }
                  }
                  return next;
                }
                return point;
              }).filter((point: any) => {
                // Filter out points with null values
                if (Array.isArray(point) && point.length >= 2) {
                  return point[1] !== null && point[1] !== undefined;
                }
                return point !== null && point !== undefined;
              });
            }
          }
        return series;
      });
    }

    // Clean ChartConfig at top level if present (legacy shape)
    if (response.ChartConfig) {
      if (response.ChartConfig.xAxis?.categories && Array.isArray(response.ChartConfig.xAxis.categories)) {
        response.ChartConfig.xAxis.categories = normalizeCategoryArray(response.ChartConfig.xAxis.categories);
      }
    }

    return response;
  }

  private cleanRequest(request: any): any {
    const cleaned: any = {};

    // Only include fields that are in the Postman collection payload
    // Exclude any fields that might cause validation errors (like categories)
    const allowedFields = [
      'chart_type',
      'metric_type',
      'start_date',
      'end_date',
      'dimension',
      'secondary_dimension',
      'filters',
      'criteria',
      'chart_title',
      'chart_subtitle',
      'x_axis_name',
      'y_axis_name',
      'time_zone'
    ];

    // Copy only allowed fields and ensure proper types
    if (request.chart_type !== undefined && request.chart_type !== null) {
      cleaned.chart_type = String(request.chart_type);
    }
    if (request.metric_type !== undefined && request.metric_type !== null) {
      cleaned.metric_type = String(request.metric_type);
    }
    if (request.start_date !== undefined && request.start_date !== null) {
      cleaned.start_date = String(request.start_date);
    }
    if (request.end_date !== undefined && request.end_date !== null) {
      cleaned.end_date = String(request.end_date);
    }
    if (request.dimension !== undefined && request.dimension !== null && request.dimension !== '') {
      cleaned.dimension = String(request.dimension);
    }
    if (request.secondary_dimension !== undefined) {
      cleaned.secondary_dimension = request.secondary_dimension === null || request.secondary_dimension === '' ? null : String(request.secondary_dimension);
    }

    // Clean filters array — Insights format only (FilterRequest: logicalOperator, columnName, operationType, value, isClusterFilter, clusterFilters)
    const valueDisabledOps = ['is_empty', 'is_not_empty'];
    if (request.filters !== undefined && Array.isArray(request.filters) && request.filters.length > 0) {
      cleaned.filters = request.filters
        .filter((f: any) => f != null && f.columnName != null && f.operationType != null)
        .map((f: any) => {
          const op = String(f.operationType);
          const value = valueDisabledOps.includes(op) ? '' : (f.value ?? '');
          const clusterFilters = Array.isArray(f.clusterFilters)
            ? f.clusterFilters.map((cf: any) => {
                const cfOp = String(cf.operationType ?? '');
                return {
                  ...cf,
                  columnValue: serializeFilterOperandValue(cfOp, cf.columnValue),
                };
              })
            : [];
          return {
            logicalOperator: f.logicalOperator ?? 'AND',
            columnName: String(f.columnName),
            operationType: op,
            value,
            isClusterFilter: Boolean(f.isClusterFilter),
            clusterFilters,
          };
        });
      cleaned.filters = normalizeFilterRequestsForBackend(cleaned.filters as FilterRequest[]);
    } else {
      cleaned.filters = [];
    }

    // Clean criteria object — drop null/empty except top_n (explicit null means user cleared Top N)
    // Only include if object has valid keys (don't send empty object)
    if (request.criteria !== undefined && request.criteria !== null && typeof request.criteria === 'object' && !Array.isArray(request.criteria)) {
      const cleanedCriteria: any = {};
      Object.keys(request.criteria).forEach(key => {
        const value = request.criteria[key];
        const excludedKeys = ['id', 'object', 'created', 'data', 'success', 'message', 'detail', 'categories', 'response'];
        if (excludedKeys.includes(key.toLowerCase())) {
          return;
        }
        if (key === 'top_n') {
          if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')) {
            cleanedCriteria.top_n = null;
          } else {
            const n = typeof value === 'number' ? value : Number(String(value).trim());
            cleanedCriteria.top_n = Number.isFinite(n) ? n : null;
          }
          return;
        }
        if (value !== null && value !== undefined && value !== '') {
          cleanedCriteria[key] = value;
        }
      });
      if (Object.keys(cleanedCriteria).length > 0) {
        cleaned.criteria = cleanedCriteria;
      }
    }
    // Don't include criteria if empty object

    if (request.chart_title !== undefined) {
      cleaned.chart_title = request.chart_title === null || request.chart_title === '' ? null : String(request.chart_title);
    }
    if (request.chart_subtitle !== undefined) {
      cleaned.chart_subtitle = request.chart_subtitle === null || request.chart_subtitle === '' ? null : String(request.chart_subtitle);
    }
    if (request.x_axis_name !== undefined) {
      cleaned.x_axis_name = request.x_axis_name === null || request.x_axis_name === '' ? null : String(request.x_axis_name);
    }
    if (request.y_axis_name !== undefined) {
      cleaned.y_axis_name = request.y_axis_name === null || request.y_axis_name === '' ? null : String(request.y_axis_name);
    }
    if (request.time_zone !== undefined && request.time_zone !== null && String(request.time_zone).trim() !== '') {
      cleaned.time_zone = String(request.time_zone).trim();
    }

    // Remove any fields that are not in the allowed list (like categories)
    Object.keys(cleaned).forEach(key => {
      if (!allowedFields.includes(key)) {
        delete cleaned[key];
      }
    });

    return cleaned;
  }

  // Save dashboard to DynamoDB
  saveDashboard(request: SaveDashboardRequest): Observable<SaveDashboardResponse> {

    try {
      const headers = this.getAuthHeaders();
      return this.http
        .post<SaveDashboardResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/save`, request, {
          headers: headers,
        })
        .pipe(
          catchError((error: any) => {
            console.error('Error saving dashboard:', error);
            console.error('Error details:', {
              status: error?.status,
              statusText: error?.statusText,
              message: error?.message,
              error: error?.error
            });
            // Re-throw error so component can handle it properly
            throw error;
          })
        );
    } catch (error: any) {
      console.error('Error setting up save dashboard request:', error);
      return new Observable(observer => {
        observer.error(error);
      });
    }
  }

  // Get dashboard details by ID (includes all chart details)
  getDashboardById(dashboardId: string): Observable<DashboardDetailsResponse> {
    return this.http
      .get<DashboardDetailsResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/${dashboardId}`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Recalculate rolling date ranges and regenerate every chart on the dashboard
  syncDashboardCharts(dashboardId: string): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/${this.customDashboardBasePath}/${dashboardId}/sync`, {}, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        catchError((error: any) => {
          console.error('Error syncing dashboard charts:', error);
          return throwError(() => error);
        })
      );
  }

  // Get all dashboards list
  getAllDashboards(): Observable<Dashboard[]> {
    return this.http
      .get<Dashboard[]>(`${this.baseUrl}/${this.customDashboardBasePath}/list`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Update dashboard
  updateDashboard(dashboardId: string, request: SaveDashboardRequest): Observable<SaveDashboardResponse> {
    return this.http
      .put<SaveDashboardResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/${dashboardId}`, request, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        catchError((error: any) => {
          console.error('Error updating dashboard:', error);
          // Re-throw error so component can handle it properly
          throw error;
        })
      );
  }

  // LocalStorage utility methods
  private readonly STORAGE_KEY = 'user_dashboard_draft';

  saveDraftToLocalStorage(dashboardName: string, charts: ChartData[], layout: string, dashboardId?: string): void {
    const draft = {
      dashboardName,
      charts,
      layout,
      dashboardId,
      timestamp: new Date().toISOString()
    };
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(draft));
  }

  getDraftFromLocalStorage(): { dashboardName: string; charts: ChartData[]; layout: string; dashboardId?: string } | null {
    const draftStr = sessionStorage.getItem(this.STORAGE_KEY);
    if (!draftStr) {
      return null;
    }
    try {
      const draft = JSON.parse(draftStr);
      return {
        dashboardName: draft.dashboardName || 'Untitled Dashboard',
        charts: draft.charts || [],
        layout: draft.layout || '2-column',
        dashboardId: draft.dashboardId
      };
    } catch (error) {
      console.error('Error parsing sessionStorage draft:', error);
      return null;
    }
  }

  clearDraftFromLocalStorage(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  // Export chart to CSV (Deprecated - synchronous)
  exportChartToCSV(chartId: string): Observable<ExportChartToCSVResponse> {
    return this.http
      .get<ExportChartToCSVResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/${chartId}/export-csv`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Create CSV export job (async - returns file_key for S3 polling)
  createExportJob(chartId: string): Observable<CreateExportJobResponse> {
    return this.http
      .post<CreateExportJobResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/${chartId}/create-export-job`, {}, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((response: any) => {
          // Handle both new wrapped format and legacy format
          if (response.success && response.data) {
            return response;
          } else if (response.bucket_name || response.file_key) {
            // Legacy format - wrap it
            return {
              success: true,
              message: 'CSV export job created.',
              data: {
                bucket_name: response.bucket_name,
                file_key: response.file_key,
                job_id: response.job_id || ''
              }
            };
          }
          return response;
        }),
        catchError((error: any) => {
          console.error('Error creating export job:', error);
          throw error;
        })
      );
  }

  // Check if CSV file is available in S3 (polling endpoint)
  checkCSVFileAvailability(request: CheckCSVFileRequest): Observable<CheckCSVFileResponse> {
    return this.http
      .post<CheckCSVFileResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/check-csv-file`, request, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((response: any) => {
          // Handle both new wrapped format and legacy format
          if (response.success && response.data) {
            // API returns ready_to_download, but we need to map it to available
            const data = response.data;
            return {
              ...response,
              data: {
                available: data.ready_to_download !== undefined ? data.ready_to_download : data.available,
                download_url: data.download_url,
                expires_at: data.expires_at
              }
            };
          } else if (response.available !== undefined || response.ready_to_download !== undefined) {
            // Legacy format - wrap it
            return {
              success: true,
              message: response.message || 'File availability checked.',
              data: {
                available: response.ready_to_download !== undefined ? response.ready_to_download : response.available,
                download_url: response.download_url,
                expires_at: response.expires_at
              }
            };
          }
          return response;
        }),
        catchError((error: any) => {
          console.error('Error checking CSV file availability:', error);
          throw error;
        })
      );
  }

  /**
   * Poll for CSV file availability
   * Polls every 5 seconds for a maximum of 40 times (total ~200 seconds)
   * @param request - The request containing bucket_name and file_key
   * @returns Observable that emits when file is available or max attempts reached
   */
  pollCSVFileAvailability(request: CheckCSVFileRequest): Observable<CheckCSVFileResponse> {
    const pollInterval = 5000; // 5 seconds in milliseconds (increased from 2.5 seconds)
    const maxAttempts = 40; // ~200 seconds total

    // Create an observable that polls until file is available or max attempts reached
    return new Observable<CheckCSVFileResponse>((observer) => {
      let attempt = 0;
      let pollingInterval: any;

      const poll = () => {
        attempt++;

        this.checkCSVFileAvailability(request).subscribe({
          next: (response) => {
            // Handle new response structure with data wrapper
            const fileData = response.data || response;
            // Check for both available and ready_to_download for compatibility
            // API returns ready_to_download, but we also support available for backward compatibility
            const isAvailable = fileData?.available === true || fileData?.ready_to_download === true;

            if (isAvailable) {
              if (pollingInterval) {
                clearInterval(pollingInterval);
              }
              observer.next(response);
              observer.complete();
            } else {
              // File not available yet, continue polling if we haven't reached max attempts
              if (attempt >= maxAttempts) {
                if (pollingInterval) {
                  clearInterval(pollingInterval);
                }
                // observer.error(new Error(`CSV file not available after ${maxAttempts} attempts (${Math.round(maxAttempts * (pollInterval / 1000))} seconds)`));
                observer.error(new Error(`File export failed.`));

              }
              // Otherwise, continue polling (interval will call poll again)
            }
          },
          error: (error) => {
            if (pollingInterval) {
              clearInterval(pollingInterval);
            }
            observer.error(error);
          }
        });
      };

      // Start polling immediately
      poll();

      // Set up interval for subsequent polls
      pollingInterval = setInterval(() => {
        if (attempt >= maxAttempts) {
          clearInterval(pollingInterval);
          //observer.error(new Error(`CSV file not available after ${maxAttempts} attempts (${maxAttempts * (pollInterval / 1000)} seconds)`));
          observer.error(new Error(`File export failed.`));
          return;
        }
        poll();
      }, pollInterval);

      // Cleanup function
      return () => {
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      };
    });
  }

  // Get chart for editing (returns chart details with all parameters)
  getChartForEditing(chartId: string): Observable<GenerateChartRequest> {
    return this.http
      .get<GenerateChartRequest>(`${this.baseUrl}/${this.customDashboardBasePath}/chart/${chartId}`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Update existing chart
  updateChart(chartId: string, request: GenerateChartRequest | any): Observable<GenerateChartResponse> {
    // Clean the request to remove any undefined values and ensure proper types
    const cleanedRequest = this.cleanRequest(request);

    return this.http
      .put<GenerateChartResponse>(`${this.baseUrl}/${this.customDashboardBasePath}/chart/${chartId}`, cleanedRequest, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        // Clean response to remove null values from categories before processing
        map((response: any) => this.cleanChartResponse(response)),
        catchError((error: any) => {
          // Never convert chart update errors to success — so no card is shown on 4xx/5xx
          if (error?.error?.detail && typeof error.error.detail === 'string' && error.error.detail.includes('categories')) {
            const friendlyError = {
              ...error,
              error: {
                ...error.error,
                message: 'Chart update failed due to invalid data. The server returned null values in categories. Please try again or contact support if the issue persists.',
                detail: 'The server returned invalid category data with null values. This is a known backend issue that needs to be fixed on the backend.'
              }
            };
            return throwError(() => friendlyError);
          }
          return throwError(() => error);
        })
      );
  }

  // Share dashboard with all users
  shareDashboard(dashboardId: string): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/${this.customDashboardBasePath}/share`, {
        dashboard_id: dashboardId,
        share_with_all: true
      }, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Unshare dashboard from all users
  unshareDashboard(dashboardId: string): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/${this.customDashboardBasePath}/unshare`, {
        dashboard_id: dashboardId,
        unshare_from_all: true
      }, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // Delete dashboard
  deleteDashboard(dashboardId: string): Observable<any> {
    return this.http
      .delete<any>(`${this.baseUrl}/${this.customDashboardBasePath}/${dashboardId}`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(catchError(this.handleError));
  }
}
